import { useEffect, useState, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { fmtDuration, fmtTime, minutesOfDay, useNow } from './use-now';

export type DayRailItem = {
  id: string;
  /** minutos desde 00:00 */
  start: number;
  end: number;
  title: string;
  subtitle?: string;
  color: string; // var(--color-label-*)
  done?: boolean;
  onOpen?: () => void;
};

const HOUR_PX = 64;

/**
 * Régua vertical do dia (ref. 04): blocos com altura proporcional à duração
 * e a linha do "agora" atravessando a régua em tempo real.
 * Ao montar, a linha desce do início do dia até a hora atual: o único movimento não acionado pelo usuário na Home.
 */
export function DayRail({
  items,
  startHour = 8,
  endHour = 20,
  empty,
  className,
}: {
  items: DayRailItem[];
  startHour?: number;
  endHour?: number;
  empty?: ReactNode;
  className?: string;
}) {
  const now = useNow();
  const [arrived, setArrived] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setArrived(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const nowMin = minutesOfDay(now);
  // Fora do expediente a régua se estende até a hora atual: a linha do agora nunca some.
  const workStart = startHour * 60;
  const workEnd = endHour * 60;
  const firstHour = Math.min(startHour, now.getHours());
  const lastHour = Math.max(endHour, Math.min(24, now.getHours() + 1));
  const dayStart = firstHour * 60;
  const dayEnd = lastHour * 60;
  const clamped = Math.min(Math.max(nowMin, dayStart), dayEnd);
  const y = (m: number) => ((m - dayStart) / 60) * HOUR_PX;
  const hours = Array.from({ length: lastHour - firstHour + 1 }, (_, i) => firstHour + i);
  const inDay = nowMin >= dayStart && nowMin <= dayEnd;
  const remaining = Math.max(0, workEnd - nowMin);

  return (
    <div className={cn('relative', className)}>
      <div className="relative" style={{ height: y(dayEnd) + 8 }}>
        {/* Horas */}
        {hours.map((h) => (
          <div key={h} className="absolute inset-x-0 flex items-start gap-4" style={{ top: y(h * 60) }}>
            <span className="tabular -mt-[9px] w-10 shrink-0 text-right text-[12px] text-fg-subtle">
              {String(h % 24).padStart(2, '0')}:00
            </span>
            <span className="h-px flex-1 bg-line" />
          </div>
        ))}

        {/* Trilho: parte já vivida do dia fica preenchida */}
        <div className="absolute top-0 bottom-2 left-[60px] w-[3px] rounded-full bg-line">
          <div
            className="w-full rounded-full bg-fg-subtle/50 transition-[height] duration-[1100ms] ease-[var(--ease-out-expo)]"
            style={{ height: arrived ? y(clamped) : 0 }}
          />
        </div>

        {/* Blocos do dia */}
        {items.map((it) => {
          const top = y(Math.max(it.start, dayStart));
          const height = Math.max(28, y(Math.min(it.end, dayEnd)) - top - 4);
          const past = it.end <= nowMin;
          return (
            <button
              key={it.id}
              type="button"
              onClick={it.onOpen}
              className={cn(
                'group absolute right-0 left-[76px] flex overflow-hidden rounded-[12px] bg-raised text-left shadow-[inset_0_0_0_1px_var(--color-line)] transition-colors hover:bg-overlay',
                past && 'opacity-55',
              )}
              style={{ top, height }}
            >
              <span className="w-1 shrink-0" style={{ background: it.color }} />
              <span className="min-w-0 px-3 py-2">
                <span
                  className={cn(
                    'block truncate text-[13.5px] font-medium',
                    it.done && 'text-fg-muted line-through',
                  )}
                >
                  {it.title}
                </span>
                <span className="tabular block truncate text-[12px] text-fg-muted">
                  {fmtTime(at(it.start))}–{fmtTime(at(it.end))}
                  {it.subtitle && <span className="ml-2 text-fg-subtle">{it.subtitle}</span>}
                </span>
              </span>
            </button>
          );
        })}

        {items.length === 0 && empty && (
          <div
            className="absolute right-0 left-[76px]"
            style={{ top: y(Math.max(dayStart, Math.min(nowMin, dayEnd - 120)) + 30) }}
          >
            {empty}
          </div>
        )}

        {/* Linha do agora */}
        {inDay && (
          <div
            aria-label={`Agora, ${fmtTime(now)}`}
            className="pointer-events-none absolute inset-x-0 z-10 flex items-center transition-[top] duration-[1100ms] ease-[var(--ease-out-expo)]"
            style={{ top: (arrived ? y(clamped) : 0) - 11 }}
          >
            <span className="tabular w-[52px] shrink-0 rounded-full bg-signal py-0.5 text-center font-mono text-[11.5px] font-semibold text-signal-fg">
              {fmtTime(now)}
            </span>
            <span className="relative ml-[1px] size-[11px] shrink-0 rounded-full bg-signal">
              <span className="absolute inset-0 animate-ping rounded-full bg-signal/60 [animation-duration:2.4s]" />
            </span>
            <span className="h-[1.5px] flex-1 bg-gradient-to-r from-signal to-signal/10" />
          </div>
        )}
      </div>

      <p className="tabular mt-4 pl-[76px] text-[12.5px] text-fg-muted">
        {nowMin < workStart
          ? `O expediente começa às ${String(startHour).padStart(2, '0')}:00`
          : remaining > 0
            ? `Faltam ${fmtDuration(remaining)} para o fim do expediente`
            : 'Expediente encerrado'}
      </p>
    </div>
  );
}

function at(minutes: number): Date {
  const d = new Date();
  d.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
  return d;
}
